import * as Sentry from '@sentry/react';

export const USER_STORAGE_USER_KEY = 'user-key';

// Old builds left a compromised static admin credential persisted in some
// browsers' localStorage (INV-0001 / TC-1000). Legitimate session tokens are
// opaque base64url strings issued by the API; the poisoned value is trivially
// distinguishable by its prefix, both raw and base64-encoded ('QURNSU5f' is
// base64 of the raw prefix). Anything matching it must never be sent as a
// bearer, so every storage read and write goes through this check — a stale
// tab running an old bundle can re-write the value at any time, which is why
// the guard is permanent rather than a one-shot migration.
export const isPoisonedCredential = (value: unknown): boolean =>
  typeof value !== 'string' ||
  /^ADMIN_/.test(value) ||
  /^QURNSU5f/.test(value);

let purgeReported = false;

const reportPurge = (source: string, meta: Record<string, unknown>) => {
  if (purgeReported) return;
  purgeReported = true;
  Sentry.captureMessage('poisoned-credential-purged', {
    level: 'warning',
    extra: { source, ...meta },
  });
};

// Test-only escape hatch for the once-per-page-load reporting flag.
export const __resetPurgeReporting = () => {
  purgeReported = false;
};

// Removes the stored credential when it is unusable or poisoned. Returns true
// when something was purged. Never throws, and never touches a legitimate
// stored token.
export const purgePoisonedStoredCredential = (source: string): boolean => {
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(USER_STORAGE_USER_KEY);
  } catch {
    return false;
  }
  if (raw === null) return false;

  let parseFailed = false;
  let value: unknown;
  try {
    value = JSON.parse(raw).value;
  } catch {
    parseFailed = true;
  }

  if (!parseFailed && !isPoisonedCredential(value)) return false;

  window.localStorage.removeItem(USER_STORAGE_USER_KEY);
  reportPurge(source, {
    parseFailed,
    valueLength: typeof value === 'string' ? value.length : null,
    hasAdminPrefix: typeof value === 'string' && /^ADMIN_/.test(value),
  });
  return true;
};
